import { ReactElement } from 'react';
import i18n from 'i18next';

/**
 * Spawn a translation function for a given prefix.
 * @param prefix - The prefix of the translation key.
 * @returns A function that returns the translation for a given key by concatenating the prefix and the key.
 */
export const spawn =
  (prefix: string) =>
  (key: string | number, options?: Record<string, unknown>) =>
    i18n.t(`${prefix}.${key}`, options);

/**
 * Gets an array of translations for a base key with numbered suffixes.
 * It checks if the translation key exists with a number suffix and will return an array of React elements with the translations wrapped in p tags.
 * If the base key is not a numbered translation, it will return an empty array + a console warning.
 * @param baseKey - The base translation key without number suffix
 * @param options - Optional translation options
 * @returns Array of React elements with translations wrapped in p tags
 */
export const renderTranslations = (
  baseKey: string,
  options?: Record<string, unknown>,
): ReactElement[] => {
  const translations: ReactElement[] = [];
  const spawnTranslation = spawn(baseKey);
  let index = 1;

  while (i18n.exists(`${baseKey}.${index}`)) {
    translations.push(<p key={index}>{spawnTranslation(index, options)}</p>);
    index++;
  }

  // Not even 1 iteration was made, so the base key is not a numbered translation
  if (index === 1) {
    console.warn(`No numbered translations found for ${baseKey}`);
  }

  return translations;
};
